/**
 * Static per-tool presentation metadata: icon (UnoCSS phosphor class), label,
 * and a one-line summary derived from the tool's input. Pure module — no React,
 * no stores — so summaries are unit-testable.
 */

export interface ToolMetaEntry {
  icon: string;
  label: string;
}

export const TOOL_META: Record<string, ToolMetaEntry> = {
  write_file: { icon: 'i-ph:file-plus', label: 'Write file' },
  edit_file: { icon: 'i-ph:pencil-simple', label: 'Edit file' },
  read_files: { icon: 'i-ph:file-text', label: 'Read files' },
  run_shell: { icon: 'i-ph:terminal', label: 'Run command' },
  db_migrate: { icon: 'i-ph:database', label: 'Migrate database' },
  list_files: { icon: 'i-ph:folder-open', label: 'List files' },
  search_code: { icon: 'i-ph:magnifying-glass', label: 'Search code' },
  glob: { icon: 'i-ph:file-search', label: 'Find files' },
  lint_check: { icon: 'i-ph:check-circle', label: 'Lint check' },
  ask_user: { icon: 'i-ph:question', label: 'Ask user' },
  component_library: { icon: 'i-ph:package', label: 'Component library' },
  task_manager: { icon: 'i-ph:list-checks', label: 'Task plan' },
  apply_diff: { icon: 'i-ph:git-diff', label: 'Apply diff' },
  bulk_write_files: { icon: 'i-ph:files', label: 'Write files' },
  insert_at_line: { icon: 'i-ph:text-indent', label: 'Insert code' },
  http_request: { icon: 'i-ph:plugs-connected', label: 'HTTP request' },
  start_server: { icon: 'i-ph:play', label: 'Start Server' },
  take_screenshot: { icon: 'i-ph:camera', label: 'Take screenshot' },
  test_backend: { icon: 'i-ph:plugs-connected', label: 'Test backend' },
  research: { icon: 'i-ph:book-open-text', label: 'Research' },
  review_ui: { icon: 'i-ph:eye', label: 'Review UI' },
  review_code: { icon: 'i-ph:code', label: 'Review code' },
  remember: { icon: 'i-ph:brain', label: 'Remember' },
};

/** Tools that spawn a sub-agent — rendered as their own nested block, never folded. */
export const SUB_AGENT_TOOLS = new Set(['test_backend', 'research', 'review_ui', 'review_code']);

/**
 * File-writing tools — surfaced as their own prominent FileCard (not folded into
 * a faint "N steps" row), so the files the agent creates/edits are the visible,
 * scannable spine of the thread.
 */
export const FILE_WRITE_TOOLS = new Set([
  'write_file',
  'bulk_write_files',
  'edit_file',
  'apply_diff',
  'insert_at_line',
]);

export function toolNameOf(part: { type: string }): string {
  return part.type.replace(/^tool-/, '');
}

export function toolMeta(name: string): ToolMetaEntry {
  return TOOL_META[name] ?? { icon: 'i-ph:wrench', label: name.replace(/_/g, ' ') };
}

/** `apps/client/src/components/chat/Foo.tsx` → `…/components/chat/Foo.tsx` */
export function shortPath(p: string | null | undefined): string {
  if (!p) {
    return '';
  }

  const parts = p.replace(/^\//, '').split('/');

  return parts.length > 3 ? `…/${parts.slice(-3).join('/')}` : parts.join('/');
}

const str = (input: Record<string, any> | undefined, key: string): string | null =>
  input && typeof input[key] === 'string' ? (input[key] as string) : null;

const countLines = (s: unknown): number =>
  typeof s === 'string' && s.length > 0 ? s.replace(/\n$/, '').split('\n').length : 0;

/**
 * A lightweight added/removed line count for a file-write tool, derived from its
 * INPUT (available immediately, no output needed). Returns null when there's
 * nothing meaningful to show.
 */
export function fileStat(
  name: string,
  input: Record<string, any> | undefined,
): { added: number; removed: number } | null {
  if (!input) {
    return null;
  }

  switch (name) {
    case 'write_file':
    case 'bulk_write_files':
    case 'insert_at_line': {
      const added = countLines(input.content);

      return added > 0 ? { added, removed: 0 } : null;
    }
    case 'edit_file': {
      const added = countLines(input.newString);
      const removed = countLines(input.oldString);

      return added > 0 || removed > 0 ? { added, removed } : null;
    }
    case 'apply_diff': {
      const diff = typeof input.diff === 'string' ? (input.diff as string) : '';

      if (!diff) {
        return null;
      }

      let added = 0;
      let removed = 0;

      for (const line of diff.split('\n')) {
        if (line.startsWith('+') && !line.startsWith('+++')) {
          added++;
        } else if (line.startsWith('-') && !line.startsWith('---')) {
          removed++;
        }
      }

      return added > 0 || removed > 0 ? { added, removed } : null;
    }
    default:
      return null;
  }
}

/** One-line summary shown next to the tool label in a ToolLine. */
export function toolSummary(name: string, input: Record<string, any> | undefined): string {
  switch (name) {
    case 'run_shell':
    case 'start_server':
      return str(input, 'command') ?? '';
    case 'write_file':
    case 'edit_file':
    case 'apply_diff':
    case 'insert_at_line':
      return shortPath(str(input, 'filePath'));
    case 'read_files': {
      const paths = Array.isArray(input?.filePaths) ? (input!.filePaths as string[]) : [];
      const first = str(input, 'filePath') ?? paths[0];
      const extra = paths.length > 1 ? ` +${paths.length - 1}` : '';

      return first ? `${shortPath(first)}${extra}` : '';
    }
    case 'search_code':
    case 'glob':
      return str(input, 'pattern') ?? str(input, 'query') ?? '';
    case 'list_files':
      return shortPath(str(input, 'path') ?? str(input, 'dirPath'));
    case 'http_request':
      return str(input, 'url') ?? '';
    case 'remember':
      return str(input, 'key') ?? '';
    case 'take_screenshot':
      return '';
    default: {
      const first = input ? (Object.values(input).find((v) => typeof v === 'string') as string | undefined) : undefined;

      return first ? first.slice(0, 120) : '';
    }
  }
}
